import { useContext, useCallback } from 'react';
import { MutatorState } from '@aesop-fables/scrinium';
import { useService } from '@aesop-fables/containr-react';
import { OrganizationData } from '../models/OrganizationData';
import { Toast } from '../models/Toast';
import { OrgStatus } from '../dashboard/multitier/AdminDashboard';
import { ToastbarContext } from '../App';
import { useLoading } from './useLoading';
import { DrillDownWizard, drillDownWizardKey } from '../services/drillDown';

interface UseNavigateToOrgParams {
  selectOrganization?: MutatorState<number>;
  setOrgStatus?: React.Dispatch<React.SetStateAction<OrgStatus | undefined>>;
  setIsNavigatingToOrg?: React.Dispatch<React.SetStateAction<boolean>>;
  selectParents: (org: OrganizationData) => Promise<void>;
}

export function useNavigateToOrg({
  selectOrganization,
  setOrgStatus,
  setIsNavigatingToOrg,
  selectParents,
}: UseNavigateToOrgParams) {
  const { setLoading } = useLoading();
  const { setToast } = useContext(ToastbarContext);
  const drillDownWizard = useService<DrillDownWizard>(drillDownWizardKey);

  return useCallback(
    async (org: OrganizationData) => {
      if (org.id === undefined) {
        setToast(
          new Toast({
            message: 'Unable to load this organization',
            severity: 'error',
            open: true,
          }),
        );
        return;
      }
      try {
        drillDownWizard.setPreNavSelectedTiers();

        setLoading(true);
        setIsNavigatingToOrg?.(true);
        await selectParents(org);
        setOrgStatus?.('complete');
        await selectOrganization?.action(org.id);
      } catch {
        setIsNavigatingToOrg?.(false);
        setOrgStatus?.(undefined);
        setLoading(false);
        setToast(
          new Toast({
            message: 'Something went wrong loading this organization',
            severity: 'error',
            open: true,
          }),
        );
      }
    },
    [
      setToast,
      setLoading,
      setIsNavigatingToOrg,
      selectParents,
      setOrgStatus,
      selectOrganization,
      drillDownWizard,
    ],
  );
}
